import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import UserProfile from 'containers/admin/users/user/user-profile/user-profile';
import { getUserActionMenuItems } from 'containers/admin/users/user/user-action-menu-items';

import { useClient, findClient } from 'hooks/api/use-clients';
import type { ClientRecord } from 'hooks/api/use-clients';
import {
  findAndAddClientData,
  useLockUser,
  useDeleteUser,
} from 'hooks/api/use-users';
import type { UserRecord } from 'hooks/api/use-users';
import { useCurrentUser } from 'hooks/api/use-current-user';
import { useImpersonation } from 'hooks/use-impersonation';

import Notification from 'components/partials/notification/notification.helper';
import { transformUserToFormObject } from 'helpers/forms';

interface NotificationParams {
  heading: string;
  variant: string;
  message: string;
}

interface UserPageProps {
  clientId?: string | number | null;
  isCurrentUserProfile?: boolean;
  notificationParams?: NotificationParams | null;
}

interface UserResponse {
  data: {
    id?: string | number;
    name?: string;
    client?: { id?: string | number } | null;
    [key: string]: unknown;
  };
}

const UserPage = ({
  clientId,
  isCurrentUserProfile = false,
  notificationParams,
}: UserPageProps): React.JSX.Element | false => {
  const [loading, setLoading] = useState(false);
  const [currentClient, setCurrentClient] = useState<ClientRecord | null>(null);
  const [user, setUser] = useState<UserResponse['data'] | null>(null);

  const currentUser = useCurrentUser();
  const impersonation = useImpersonation();
  const params = useParams();

  const isNewUser = params.id === 'new';
  const [isFormDisabled, setIsFormActive] = useState(!isNewUser);
  const navigate = useNavigate();
  const lockUserMutation = useLockUser();
  const deleteUserMutation = useDeleteUser();

  const userId = isCurrentUserProfile ? currentUser.data?.id : params.id;

  const { data: clientFromProp } = useClient(clientId);

  const loadUser = (): void => {
    if (!userId || userId === 'new') {
      return;
    }

    setLoading(true);

    findAndAddClientData(userId).then(({ data }) => {
      if (data?.client?.id) {
        findClient(data.client.id).then((client) => {
          setCurrentClient(client.data);
        });
      }
      setUser(data);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (userId && userId !== 'new') {
      loadUser();
    }
  }, [userId]);

  useEffect(() => {
    if (isCurrentUserProfile) setUser(currentUser.data as UserResponse['data']);
  }, [isCurrentUserProfile]);

  useEffect(() => {
    if (clientFromProp) {
      setCurrentClient(clientFromProp);
    }
  }, [clientFromProp]);

  useEffect(() => {
    if (notificationParams)
      Notification.flash(
        notificationParams.heading,
        notificationParams.variant,
        notificationParams.message
      );
  }, []);

  return ((currentClient || user) && (
    <UserProfile
      {...{
        pageName: user?.name,
        currentClient,
        isImpersonatingUser: !!impersonation.impersonatingUser,
        user,
        isCurrentUserProfile,
        isFormDisabled,
        setIsFormActive,
        loading,
        reloadUser: loadUser,
        actionMenuItems:
          userId !== 'new' &&
          !isCurrentUserProfile &&
          getUserActionMenuItems({
            user: user as never,
            currentClient,
            reloadUser: loadUser,
            currentUser,
            impersonation,
            navigate,
            lockUser: (u) => lockUserMutation.mutateAsync(u as UserRecord),
            deleteUser: (id) => deleteUserMutation.mutateAsync({ id }),
          }),
        defaultValues: transformUserToFormObject(
          user as never,
          currentClient as never
        ),
      }}
    />
  )) as React.JSX.Element | false;
};

export default UserPage;
